// app/src/account-attachments/components/AttachmentsSection.tsx
import { useEffect, useState } from "react";
import type { AccountAttachment } from "wasp/entities";
import { useQuery, useAction } from "wasp/client/operations";
import {
  getAccountAttachments,
  getAccountAttachmentSignedURL,
  deleteAccountAttachment,
} from "wasp/client/operations";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../client/components/ui/card";
import { Button } from "../../client/components/ui/button";
import { Badge } from "../../client/components/ui/badge";
import { FileText, Trash2, Eye, Plus, X } from "lucide-react";
import useTranslation from "../../client/hooks/useTranslation";
import { AttachmentUploadDialog } from "./AttachmentUploadDialog";

type Props = { accountId: string };

type Translations = ReturnType<typeof useTranslation>;

// `type` est une string libre en BDD : les valeurs inconnues (anciens
// enregistrements) retombent sur le libellé « Autre ».
function attachmentTypeLabel(type: string, t: Translations): string {
  switch (type) {
    case "challenge":
      return t.attachments.typeChallenge;
    case "payout":
      return t.attachments.typePayout;
    case "invoice":
      return t.attachments.typeInvoice;
    default:
      return t.attachments.typeOther;
  }
}

export function AttachmentsSection({ accountId }: Props) {
  const t = useTranslation();
  const { data: attachments, refetch } = useQuery(getAccountAttachments, { accountId });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [preview, setPreview] = useState<{ url: string; mimeType: string } | null>(null);

  const deleteAction = useAction(deleteAccountAttachment);

  async function handlePreview(id: string, mimeType: string) {
    const url = await getAccountAttachmentSignedURL({ id });
    setPreview({ url, mimeType });
  }

  async function handleDelete(id: string) {
    if (!window.confirm(t.attachments.deleteConfirm)) return;
    await deleteAction({ id });
    refetch();
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>{t.attachments.sectionTitle}</CardTitle>
        <Button size="sm" onClick={() => setUploadOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          {t.attachments.uploadButton}
        </Button>
      </CardHeader>
      <CardContent>
        {!attachments || attachments.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t.attachments.empty}</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {attachments.map((a) => (
              <AttachmentCard
                key={a.id}
                attachment={a}
                onPreview={() => handlePreview(a.id, a.mimeType)}
                onDelete={() => handleDelete(a.id)}
              />
            ))}
          </div>
        )}
      </CardContent>

      <AttachmentUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        accountId={accountId}
        onSuccess={refetch}
      />

      {preview && (
        <PreviewOverlay
          url={preview.url}
          mimeType={preview.mimeType}
          onClose={() => setPreview(null)}
        />
      )}
    </Card>
  );
}

function AttachmentCard({
  attachment,
  onPreview,
  onDelete,
}: {
  attachment: AccountAttachment;
  onPreview: () => void;
  onDelete: () => void;
}) {
  const t = useTranslation();
  const isImage = attachment.mimeType.startsWith("image/");
  const isPdf = attachment.mimeType === "application/pdf";
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isImage && !isPdf) return;
    let cancelled = false;
    getAccountAttachmentSignedURL({ id: attachment.id })
      .then((u) => {
        if (!cancelled) setThumbUrl(u);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [attachment.id, isImage, isPdf]);

  return (
    <div className="border rounded-lg p-2 space-y-2 group bg-card">
      <div
        className="aspect-[4/3] bg-muted rounded flex items-center justify-center overflow-hidden cursor-pointer"
        onClick={onPreview}
      >
        {isImage && thumbUrl ? (
          <img src={thumbUrl} alt={attachment.name} className="object-contain w-full h-full" />
        ) : isPdf && thumbUrl ? (
          // Aperçu non interactif : iframe scrollable désactivée, ouverture via onPreview
          <div className="relative w-full h-full">
            <iframe
              src={`${thumbUrl}#toolbar=0&navpanes=0&view=FitH`}
              className="w-full h-full pointer-events-none bg-white"
              title={attachment.name}
              tabIndex={-1}
            />
          </div>
        ) : (
          <FileText className="h-12 w-12 text-muted-foreground" />
        )}
      </div>
      <Badge variant="secondary" className="text-[10px] font-normal">
        {attachmentTypeLabel(attachment.type, t)}
      </Badge>
      <p className="text-xs truncate" title={attachment.name}>
        {attachment.name}
      </p>
      <div className="flex gap-1">
        <Button size="sm" variant="ghost" onClick={onPreview} className="flex-1">
          <Eye className="h-3 w-3" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete}>
          <Trash2 className="h-3 w-3 text-destructive" />
        </Button>
      </div>
    </div>
  );
}

function PreviewOverlay({
  url,
  mimeType,
  onClose,
}: {
  url: string;
  mimeType: string;
  onClose: () => void;
}) {
  // Close on Escape key
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded"
        aria-label="Close"
      >
        <X className="h-6 w-6" />
      </button>
      <div
        className="w-full h-full max-w-5xl max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {mimeType === "application/pdf" ? (
          <iframe
            src={url}
            className="w-full h-full rounded bg-white"
            title="Document preview"
          />
        ) : (
          <img src={url} alt="" className="w-full h-full object-contain" />
        )}
      </div>
    </div>
  );
}
